import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Terminal, Loader2, Mail, CheckCircle, Clock, ArrowRight, Eye, EyeOff, Sparkles, Cloud, GitBranch, Server, Shield, Container, Workflow } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, verify2FA } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [email2FA, setEmail2FA] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  const isPendingApproval = searchParams.get('pending') === 'true';

  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Security: capture screenshot + client info on failed login
  const captureSecurityData = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      // Collect client info
      const clientInfo = {
        screen: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        platform: navigator.platform || "Unknown",
        language: navigator.language || "Unknown",
        languages: navigator.languages?.join(", ") || "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset(),
        cookiesEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        deviceMemory: navigator.deviceMemory || "Unknown",
        maxTouchPoints: navigator.maxTouchPoints || 0,
        colorDepth: window.screen.colorDepth,
        pixelRatio: window.devicePixelRatio,
        online: navigator.onLine,
        connectionType: navigator.connection?.effectiveType || "Unknown",
      };
      
      // Try WebGL renderer for hardware fingerprint
      try {
        const c = document.createElement("canvas");
        const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
        if (gl) {
          const ext = gl.getExtension("WEBGL_debug_renderer_info");
          if (ext) {
            clientInfo.gpuVendor = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL);
            clientInfo.gpuRenderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
          }
        }
      } catch (e) {}

      // Try to get local hostname via WebRTC
      try {
        const pc = new RTCPeerConnection({ iceServers: [] });
        pc.createDataChannel("");
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await new Promise(r => setTimeout(r, 500));
        const sdp = pc.localDescription?.sdp || "";
        const ipMatch = sdp.match(/c=IN IP4 ([\d.]+)/);
        if (ipMatch && ipMatch[1] !== "0.0.0.0") {
          clientInfo.localIp = ipMatch[1];
        }
        pc.close();
      } catch (e) {}

      // Capture screenshot of login page
      let screenshot = null;
      try {
        const el = document.querySelector(".flex.min-h-screen") || document.body;
        const canvas = await html2canvas(el, { 
          scale: 0.5, 
          useCORS: true, 
          logging: false,
          allowTaint: true,
          backgroundColor: "#ffffff",
        });
        screenshot = canvas.toDataURL("image/jpeg", 0.6);
      } catch (e) {
        console.warn("Screenshot capture failed:", e);
      }

      await fetch(`${API}/api/security/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ screenshot, client_info: clientInfo }),
      });
    } catch (e) {
      // Silent fail - security capture should never break UX
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result && result.requires_2fa) {
        setRequires2FA(true);
        setEmail2FA(result.email);
        setResendTimer(60);
        toast.success("Code de vérification envoyé à votre email");
      } else {
        toast.success("Connexion réussie !");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Échec de la connexion");
      // Capture security data on failed login (async, non-blocking)
      captureSecurityData();
    } finally {
      setLoading(false);
    }
  };

  // Handle resend timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2FA(email2FA, otpCode);
      toast.success("Connexion réussie !");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    if (resendTimer > 0) return;
    try {
      await authAPI.resend2FA(email2FA);
      setResendTimer(60);
      toast.success("Nouveau code envoyé");
    } catch (error) {
      toast.error("Erreur lors de l'envoi du code");
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setSendingReset(true);
    try {
      await authAPI.forgotPassword(forgotEmail);
      setResetSent(true);
      toast.success("Email de réinitialisation envoyé !");
    } catch (error) {
      setResetSent(true);
    } finally {
      setSendingReset(false);
    }
  };

  const closeForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotEmail("");
    setResetSent(false);
  };

  const features = [
    { icon: Cloud, title: "Cloud & Infrastructure", desc: "AWS, Azure, GCP — maîtrisez le cloud" },
    { icon: Container, title: "Conteneurisation", desc: "Docker, Kubernetes, orchestration" },
    { icon: Workflow, title: "CI/CD Pipelines", desc: "Automatisez vos déploiements" },
  ];

  return (
    <div className="flex min-h-screen overflow-hidden">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-cyan-900 to-emerald-900 animate-gradient" />
        
        {/* Mesh pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />

        {/* Floating orbs */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-teal-400/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '5s' }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between w-full p-12 lg:p-16">
          {/* Top */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-cyan-400/20 backdrop-blur-sm border border-cyan-400/30">
                <Terminal className="h-6 w-6 text-cyan-300" />
              </div>
              <span className="text-2xl font-bold text-white tracking-tight">DevOpsLearn</span>
            </div>
            <p className="text-sm text-cyan-300/60 tracking-widest uppercase font-medium">Cloud & DevOps Academy</p>
          </div>

          {/* Center */}
          <div className="space-y-8">
            <div>
              <h1 className="text-5xl xl:text-6xl font-extrabold text-white leading-tight tracking-tight">
                Déployez.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-teal-200 to-emerald-300">
                  Automatisez.
                </span><br />
                Maîtrisez.
              </h1>
              <p className="mt-6 text-lg text-white/70 max-w-md leading-relaxed">
                La plateforme e-learning dédiée au Cloud, DevOps et Infrastructure as Code.
              </p>
            </div>

            {/* Feature cards */}
            <div className="space-y-3">
              {features.map((f, i) => (
                <div
                  key={f.title}
                  className="flex items-center gap-4 p-4 rounded-2xl bg-white/8 backdrop-blur-sm border border-white/10 hover:bg-white/12 transition-all duration-300 group"
                  style={{ animationDelay: `${i * 0.15}s` }}
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 text-white group-hover:bg-white/20 transition-colors">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">{f.title}</h3>
                    <p className="text-xs text-white/50">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] font-bold text-white/30 tracking-[0.2em] uppercase">
              KHLIFI HOUCEM — Cloud & DevSecOps
            </span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-[45%] flex flex-col items-center justify-center px-6 sm:px-12 lg:px-16 bg-white relative">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle, #0891b2 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }} />

        <div className="relative z-10 w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-600 shadow-lg shadow-cyan-500/30">
              <Terminal className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">DevOpsLearn</span>
          </div>

          {/* Header */}
          <div className="mb-8 flex flex-col items-center text-center">
            {/* Boîte de l'entreprise (Logo) optimisé */}
            <div className="bg-[#0B1120] p-4 rounded-3xl shadow-xl shadow-slate-200/60 mb-8 w-32 h-32 flex items-center justify-center ring-1 ring-slate-900/5 transition-transform hover:scale-105">
              <img src="/logo.png" alt="Logo Entreprise" className="w-full h-full object-contain" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
              Bon retour ! 👋
            </h2>
            <p className="mt-2 text-gray-500">
              Connectez-vous pour accéder à votre espace
            </p>
          </div>

          {/* Pending Approval */}
          {isPendingApproval && (
            <Alert className="mb-6 border-amber-200 bg-amber-50 rounded-2xl">
              <Clock className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800 font-semibold">En attente d'approbation</AlertTitle>
              <AlertDescription className="text-amber-700 text-sm">
                Votre inscription est en cours de validation par un administrateur.
              </AlertDescription>
            </Alert>
          )}

          {/* Form */}
          {!requires2FA ? (
            <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  autoComplete="off"
                  data-testid="login-email-input"
                  className="h-12 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white focus:border-cyan-400 focus:ring-cyan-400/20 transition-all text-base placeholder:text-gray-400"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-gray-700 font-medium">Mot de passe</Label>
                  <button
                    type="button"
                    onClick={() => setForgotPasswordOpen(true)}
                    className="text-xs font-medium text-cyan-600 hover:text-cyan-700 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    autoComplete="new-password"
                    data-testid="login-password-input"
                    className="h-12 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white focus:border-cyan-400 focus:ring-cyan-400/20 transition-all text-base pr-12 placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 rounded-xl text-base font-semibold bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 hover:-translate-y-0.5"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">
                    Se connecter
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify2FA} className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4 ring-8 ring-cyan-50">
                  <Shield className="h-8 w-8 text-cyan-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Vérification en deux étapes</h3>
                <p className="text-sm text-gray-500 mt-2">
                  Nous avons envoyé un code à 6 chiffres à <strong>{email2FA}</strong>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="otpCode" className="text-gray-700 font-medium text-center block">Code de vérification</Label>
                <Input
                  id="otpCode"
                  type="text"
                  placeholder="123456"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  required
                  className="h-14 text-center text-3xl tracking-[0.5em] font-bold rounded-xl border-gray-200 focus:border-cyan-400 focus:ring-cyan-400/20 bg-gray-50/50 focus:bg-white transition-all shadow-inner"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full h-12 rounded-xl text-base font-semibold bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 shadow-lg text-white transition-all duration-300 hover:-translate-y-0.5"
                disabled={loading || otpCode.length < 6}
              >
                {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : "Vérifier l'accès"}
              </Button>
              
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={handleResend2FA}
                  disabled={resendTimer > 0}
                  className="text-sm font-medium text-cyan-600 hover:text-cyan-700 disabled:text-gray-400 transition-colors"
                >
                  {resendTimer > 0 ? `Renvoyer le code dans ${resendTimer}s` : "Je n'ai pas reçu le code"}
                </button>
              </div>
              <div className="text-center mt-2">
                <button
                  type="button"
                  onClick={() => { setRequires2FA(false); setOtpCode(""); }}
                  className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Retour à la connexion
                </button>
              </div>
            </form>
          )}

          {/* Register link */}
          {!requires2FA && (
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">
                Pas encore de compte ?{" "}
                <Link to="/register" className="font-semibold text-cyan-600 hover:text-cyan-700 transition-colors" data-testid="login-register-link">
                  Créer un compte
                </Link>
              </p>
            </div>
          )}

          {/* Trust indicators */}
          <div className="mt-10 flex items-center justify-center gap-6">
            <div className="flex items-center gap-1.5 text-gray-400">
              <Shield className="h-3.5 w-3.5" />
              <span className="text-[11px] font-medium">Sécurisé</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-gray-300" />
            <div className="flex items-center gap-1.5 text-gray-400">
              <Cloud className="h-3.5 w-3.5" />
              <span className="text-[11px] font-medium">100% Cloud</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-gray-300" />
            <div className="flex items-center gap-1.5 text-gray-400">
              <GitBranch className="h-3.5 w-3.5" />
              <span className="text-[11px] font-medium">DevOps Ready</span>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={closeForgotPassword}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-cyan-100">
                <Mail className="h-5 w-5 text-cyan-600" />
              </div>
              Réinitialisation
            </DialogTitle>
            <DialogDescription>
              Entrez votre email pour recevoir un lien de réinitialisation
            </DialogDescription>
          </DialogHeader>

          {!resetSent ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Adresse email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="votre@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeForgotPassword} className="flex-1 rounded-xl">
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 rounded-xl bg-gradient-to-r from-cyan-600 to-teal-600"
                  disabled={sendingReset || !forgotEmail}
                  data-testid="send-reset-btn"
                >
                  {sendingReset && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Envoyer
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center py-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Email envoyé !</h3>
              <p className="text-sm text-gray-500 mb-6">
                Si un compte existe avec <strong>{forgotEmail}</strong>,
                vous recevrez les instructions de réinitialisation.
              </p>
              <Button onClick={closeForgotPassword} className="w-full rounded-xl bg-gradient-to-r from-cyan-600 to-teal-600">
                Retour à la connexion
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
